import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Button,
  theme,
  Icon as BsIcon,
  ANNOUNCEMENT_STATUS,
  ANNOUNCEMENT_TYPE,
  BANNER_ICONS,
  DISMISS_BEHAVIOR,
} from 'bs-unified-ui';
import { Form, Formik } from 'formik';
import DataLoading from '~/components/common/DataLoading';
import DialogSave from '~/components/common/DialogSave';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { Icon } from '~/components/common/Icon';
import {
  BANNER_COLOR_DEFAULT,
  DISPLAY_FORMAT,
  TIME_HOUR_DISPLAY_FORMAT,
  POS_TYPE,
  PROVINCE_TYPE,
  TIMEZONE,
} from '~/constants';
import { useMemo, useState } from 'react';
import { utcToZonedTime, format } from 'date-fns-tz';
import moment from 'moment-timezone';
import {
  useCreateAnnouncementMutation,
  useGetAnnouncementDetail,
  useUpdateAnnouncementMutation,
  useDeleteAnnouncementMutation,
  useEndPostAnnouncementMutation,
} from '~/services/api/announcement';
import { queryClient } from '~/lib/react-query';
import { showErrorDialog } from '~/components/common/Dialog';
import { getErrorMessage } from '~/utils/common';
import { showToast } from '~/components/common/Toast';
import { createAnnouncementValidationSchema } from '~/utils/schema/announcement';
import { queryKeys } from '~/services/api/queryKeys';
import { isEmpty } from 'lodash';
import { generateThumbnailFromElement } from '~/utils/image';
import NameSection from './sections/NameSection';
import FormatSection from './sections/FormatSection';
import MessageSection from './sections/MessageSection';
import SchedulingSection from './sections/SchedulingSection';
import Layout from '../components/Layout';
import ApplyToClientsSection from './sections/ApplyToClientsSection';
import { AnnouncementDetailProvider } from './AnnouncementDetailContext';
import Preview from './sections/Preview';
import TimelineSection from './sections/TimelineSection';
import ConfirmDialog from '../components/ConfirmDialog';
import ThumbnailGenerator from './sections/ThumbnailGenerator';

const CreateOrEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const duplicatedValues = (location.state as any)?.duplicatedValues;
  const [openPreview, setOpenPreview] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEndPostDialog, setOpenEndPostDialog] = useState(false);
  const [generateThumbnail, setGenerateThumbnail] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<any>(null);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);

  const {
    mutate: createAnnouncementMutation,
    isPending: isCreateAnnouncementLoading,
  } = useCreateAnnouncementMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.announcement.detail],
      });
    },
    onError: () => {},
  });

  const {
    mutate: updateAnnouncementMutation,
    isPending: isUpdateAnnouncementLoading,
  } = useUpdateAnnouncementMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.announcement.detail],
      });
    },
    onError: () => {},
  });

  const {
    mutate: deleteAnnouncementMutation,
    isPending: isDeleteAnnouncementLoading,
  } = useDeleteAnnouncementMutation({
    onSuccess: () => {
      showToast('Delete announcement successfully');
      navigate('/announcement');
    },
    onError: (e) => {
      showErrorDialog({
        title: 'Delete Failed',
        message: getErrorMessage(e),
        maxWidth: 'min(1184px, 70vw)',
      });
    },
  });

  const {
    mutate: endPostAnnouncementMutation,
    isPending: isEndPostAnnouncementLoading,
  } = useEndPostAnnouncementMutation({
    onSuccess: () => {
      showToast('End announcement successfully');
      navigate('/announcement');
    },
    onError: (e) => {
      showErrorDialog({
        title: 'End Post Failed',
        message: getErrorMessage(e),
        maxWidth: 'min(1184px, 70vw)',
      });
    },
  });

  const {
    data: announcementDetail,
    isFetching: isGetAnnouncementDetailLoading,
  } = useGetAnnouncementDetail({
    enabled: !!id,
    id,
  });

  const getNextQuarterHour = () => {
    const now = new Date();
    const pacificNow = utcToZonedTime(now, TIMEZONE);

    const minutes = pacificNow.getMinutes();
    const nextQuarterMinutes = Math.ceil((minutes + 1) / 15) * 15;

    let hours = pacificNow.getHours();
    let nextMinutes = nextQuarterMinutes;

    // Handle hour overflow
    if (nextMinutes === 60) {
      hours = (hours + 1) % 24;
      nextMinutes = 0;
    }

    // Format as HH:mm
    return `${String(hours).padStart(2, '0')}:${String(nextMinutes).padStart(
      2,
      '0',
    )}`;
  };

  const getCurrentPacificDate = () => {
    const now = new Date();
    const pacificNow = utcToZonedTime(now, TIMEZONE);
    // Convert DISPLAY_FORMAT from 'MMM D, YYYY' to 'MMM d, yyyy' for date-fns
    const dateFormatForDateFns = DISPLAY_FORMAT.replace(/D/g, 'd').replace(
      /YYYY/g,
      'yyyy',
    );
    return format(pacificNow, dateFormatForDateFns);
  };

  const combineDateTimeToISO = (date: string, time: string): string | null => {
    if (!date || !time) return null;

    try {
      // Parse using moment with Pacific timezone
      const dateTimeStr = moment.tz(
        `${date} ${time}`,
        `${DISPLAY_FORMAT} ${TIME_HOUR_DISPLAY_FORMAT}`,
        TIMEZONE,
      );

      if (!dateTimeStr.isValid()) {
        return null;
      }

      // Return format: YYYY-MM-DDTHH:mm:ss
      return dateTimeStr.format('YYYY-MM-DDTHH:mm:ss');
    } catch {
      return null;
    }
  };

  const handleSubmit = (values) => {
    // Trigger thumbnail generation and show loading
    setIsGeneratingThumbnail(true);
    setPendingSubmitValues(values);
    setGenerateThumbnail(true);
  };

  const handleThumbnailGenerated = async (element: HTMLElement) => {
    if (!pendingSubmitValues) return;

    try {
      // Generate thumbnail from element
      const thumbnailFile = await generateThumbnailFromElement(element);

      const formDataToSend = new FormData();

      // Add text fields
      formDataToSend.append('name', pendingSubmitValues.name || '');
      formDataToSend.append('type', pendingSubmitValues.type || '');
      formDataToSend.append(
        'dismiss_behaviors',
        pendingSubmitValues.dismiss_behaviors || '',
      );
      formDataToSend.append(
        'banner_color',
        pendingSubmitValues.banner_color || '',
      );
      formDataToSend.append('icon', pendingSubmitValues.icon || '');
      formDataToSend.append(
        'message_title',
        pendingSubmitValues.message_title || '',
      );
      formDataToSend.append(
        'message_description',
        pendingSubmitValues.message_description || '',
      );
      formDataToSend.append(
        'apply_to_pos',
        pendingSubmitValues.apply_to_pos || '',
      );
      formDataToSend.append(
        'apply_to_provinces_type',
        pendingSubmitValues.apply_to_provinces_type || '',
      );
      formDataToSend.append(
        'start_time',
        combineDateTimeToISO(
          pendingSubmitValues.start_date,
          pendingSubmitValues.start_hour,
        ) || '',
      );
      formDataToSend.append(
        'end_time',
        combineDateTimeToISO(
          pendingSubmitValues.end_date,
          pendingSubmitValues.end_hour,
        ) || '',
      );

      // Add provinces array as JSON string (extract labels only)
      const provinces =
        pendingSubmitValues?.apply_to_provinces?.map((p: any) => p.label) || [];
      formDataToSend.append('apply_to_provinces', JSON.stringify(provinces));

      // Add thumbnail
      formDataToSend.append('thumbnail', thumbnailFile);

      // Add image file if present (only if it's a new file, not a URL from backend)
      // Only send image field if it's a File object (new upload)
      // Don't send empty image field - only send if there's an actual File
      if (
        (pendingSubmitValues.image instanceof File &&
          pendingSubmitValues.type === ANNOUNCEMENT_TYPE.RICH_POP_UP) ||
        duplicatedValues?.image
      ) {
        formDataToSend.append('image', pendingSubmitValues.image);
      }

      if (
        pendingSubmitValues?.image_file_name &&
        pendingSubmitValues.type === ANNOUNCEMENT_TYPE.RICH_POP_UP
      ) {
        formDataToSend.append(
          'image_file_name',
          pendingSubmitValues.image_file_name,
        );
      }

      const isEdit = !!id;

      if (isEdit) {
        // Update announcement
        formDataToSend.append('id', id);
        updateAnnouncementMutation(formDataToSend, {
          onSuccess: () => {
            showToast('Update announcement successfully');
            navigate(-1);
          },
          onError: (e) => {
            showErrorDialog({
              title: 'Update Failed',
              message: getErrorMessage(e),
              maxWidth: 'min(1184px, 70vw)',
            });
            setIsGeneratingThumbnail(false);
          },
        });
      } else {
        // Create announcement
        createAnnouncementMutation(formDataToSend, {
          onSuccess: () => {
            showToast('Create announcement successfully');
            navigate('/announcement');
          },
          onError: (e) => {
            showErrorDialog({
              title: 'Create Failed',
              message: getErrorMessage(e),
              maxWidth: 'min(1184px, 70vw)',
            });
            setIsGeneratingThumbnail(false);
          },
        });
      }
    } catch (error) {
      showErrorDialog({
        title: 'Thumbnail Generation Failed',
        message: 'Failed to generate thumbnail image',
        maxWidth: 'min(1184px, 70vw)',
      });
      setIsGeneratingThumbnail(false);
    } finally {
      setGenerateThumbnail(false);
      setPendingSubmitValues(null);
    }
  };

  const initialValues = useMemo(() => {
    if (!isEmpty(announcementDetail)) {
      const startDateTime = moment(announcementDetail.start_time);
      const endDateTime = moment(announcementDetail.end_time);

      const dateFormatForDateFns = DISPLAY_FORMAT.replace(/D/g, 'd').replace(
        /YYYY/g,
        'yyyy',
      );

      return {
        name: announcementDetail.name || '',
        type: announcementDetail.type || ANNOUNCEMENT_TYPE.GLOBAL_BANNER,
        dismiss_behaviors:
          announcementDetail.dismiss_behaviors ||
          DISMISS_BEHAVIOR.TEMPORARY_DISMISS,
        banner_color:
          announcementDetail.banner_color ||
          BANNER_COLOR_DEFAULT[ANNOUNCEMENT_TYPE.GLOBAL_BANNER],
        icon: announcementDetail.icon || BANNER_ICONS[0],
        message_title: announcementDetail.message_title || '',
        message_description: announcementDetail.message_description || '',
        image: announcementDetail.image || null,
        apply_to_pos: announcementDetail.apply_to_pos || POS_TYPE.ALL,
        apply_to_provinces_type:
          announcementDetail.apply_to_provinces_type || PROVINCE_TYPE.ALL,
        start_date: format(startDateTime.toDate(), dateFormatForDateFns),
        start_hour: startDateTime.format(TIME_HOUR_DISPLAY_FORMAT),
        end_date: format(endDateTime.toDate(), dateFormatForDateFns),
        end_hour: endDateTime.format(TIME_HOUR_DISPLAY_FORMAT),
        apply_to_provinces: (() => {
          if (!Array.isArray(announcementDetail.apply_to_provinces)) {
            return [];
          }
          return announcementDetail.apply_to_provinces.map((province) => ({
            value: province,
            label: province,
          }));
        })(),
      };
    }

    // Use duplicated values when creating a duplicate announcement
    if (duplicatedValues) {
      return duplicatedValues;
    }

    return {
      name: '',
      type: ANNOUNCEMENT_TYPE.GLOBAL_BANNER,
      dismiss_behaviors: DISMISS_BEHAVIOR.TEMPORARY_DISMISS,
      banner_color: BANNER_COLOR_DEFAULT[ANNOUNCEMENT_TYPE.GLOBAL_BANNER],
      icon: BANNER_ICONS[0],
      message_title: '',
      message_description: '',
      image: null,
      apply_to_pos: POS_TYPE.ALL,
      apply_to_provinces_type: PROVINCE_TYPE.ALL,
      start_date: getCurrentPacificDate(),
      start_hour: getNextQuarterHour(),
      end_date: '',
      end_hour: '',
      apply_to_provinces: [],
    };
  }, [announcementDetail, duplicatedValues]);

  // Memoize validation schema with live status check
  const validationSchema = useMemo(() => {
    // Determine if announcement is live
    let isLiveAnnouncement = false;
    if (id && announcementDetail) {
      const start_time = announcementDetail?.start_time;
      const end_time = announcementDetail?.end_time;
      if (start_time && end_time) {
        const startDateTime = moment.tz(start_time, TIMEZONE);
        const endDateTime = moment.tz(end_time, TIMEZONE);
        const now = moment.tz(TIMEZONE);
        isLiveAnnouncement =
          now.isAfter(startDateTime) && now.isBefore(endDateTime);
      }
    }

    return createAnnouncementValidationSchema(isLiveAnnouncement);
  }, [id, announcementDetail]);

  // Get announcement status from API
  const announcementStatus = announcementDetail?.status || null;

  const handleDeleteAnnouncement = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (id) {
      deleteAnnouncementMutation(id);
      setOpenDeleteDialog(false);
    }
  };

  const handleEndPostNow = () => {
    setOpenEndPostDialog(true);
  };

  const handleConfirmEndPost = () => {
    if (id) {
      endPostAnnouncementMutation(id);
      setOpenEndPostDialog(false);
    }
  };

  const handleDuplicate = () => {
    if (id && announcementDetail) {
      // Create new values with announcement data, excluding scheduling fields
      const newDuplicatedValues = {
        name: `Copy of ${announcementDetail.name}`,
        type: announcementDetail.type,
        dismiss_behaviors: announcementDetail.dismiss_behaviors,
        banner_color: announcementDetail.banner_color,
        icon: announcementDetail.icon,
        message_title: announcementDetail.message_title,
        message_description: announcementDetail.message_description,
        image: announcementDetail.image,
        apply_to_pos: announcementDetail.apply_to_pos,
        apply_to_provinces_type: announcementDetail.apply_to_provinces_type,
        apply_to_provinces:
          announcementDetail.apply_to_provinces?.map((province) => ({
            value: province,
            label: province,
          })) || [],
        image_file_name: announcementDetail?.image_file_name,
        // Reset scheduling to default values
        start_date: getCurrentPacificDate(),
        start_hour: getNextQuarterHour(),
        end_date: '',
        end_hour: '',
      };

      // Navigate to create page with duplicated data in state
      navigate('/announcement/create', {
        state: { duplicatedValues: newDuplicatedValues },
      });
    }
  };

  return (
    <Layout
      title={
        <Stack direction='row' alignItems='center'>
          <Button iconOnly variant='text' onClick={() => navigate(-1)}>
            <BsIcon name='arrow-left-s' size={20} />
          </Button>
          <Typography
            variant='fs20'
            fontWeight={700}
            color={theme.foreground.black}
          >
            {id
              ? announcementDetail?.name
              : duplicatedValues?.name || 'Create announcement'}
          </Typography>
        </Stack>
      }
      action={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Button
            variant='text'
            type='submit'
            onClick={() => setOpenPreview(true)}
            sx={{
              fontWeight: 600,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Icon size={16} name='eye-line' color={theme.foreground.black} />
            Preview
          </Button>
          {announcementStatus === 'scheduled' && (
            <Button
              variant='text'
              onClick={handleDeleteAnnouncement}
              disabled={isDeleteAnnouncementLoading}
              sx={{
                fontWeight: 600,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
                color: '#F24822 !important',
              }}
            >
              <Icon size={16} name='delete' color='#F24822' />
              Delete
            </Button>
          )}
          {announcementStatus === 'live' && (
            <Button
              variant='text'
              onClick={handleEndPostNow}
              disabled={isEndPostAnnouncementLoading}
              sx={{
                fontWeight: 600,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
                color: '#F24822 !important',
              }}
            >
              <Icon size={16} name='stop' color='#F24822' />
              End post now
            </Button>
          )}
          {announcementStatus === 'archived' && (
            <Button
              variant='text'
              onClick={handleDuplicate}
              sx={{
                fontWeight: 600,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Icon size={16} name='copy' color={theme.foreground.black} />
              Duplicate
            </Button>
          )}
        </Box>
      }
      sx={{
        'minHeight': 'unset',
        'height': '100%',
        'overflowY': 'hidden',

        '>.MuiContainer-root': {
          'height': '100%',
          '>.MuiStack-root': { height: '100%' },
        },
      }}
    >
      <AnnouncementDetailProvider announcementDetail={announcementDetail}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values, resetForm, submitForm, dirty }) => (
            <Form
              style={{
                marginBottom:
                  announcementStatus === ANNOUNCEMENT_STATUS.ARCHIVED || !dirty
                    ? 10
                    : 70,
                overflowY: 'auto',
                position: 'relative',
              }}
              className='customized-scrollbar'
            >
              <DataLoading
                loading={
                  isCreateAnnouncementLoading ||
                  isGetAnnouncementDetailLoading ||
                  isUpdateAnnouncementLoading
                }
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Stack
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <NameSection />
                    <FormatSection />
                    <MessageSection />
                    {id && (
                      <Box sx={{ mt: 2 }}>
                        <TimelineSection announcementId={id} />
                      </Box>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <SchedulingSection />
                    <ApplyToClientsSection />
                  </Stack>
                </Grid>
              </Grid>
              <DialogSave
                open={
                  (dirty || !!duplicatedValues) &&
                  announcementStatus !== ANNOUNCEMENT_STATUS.ARCHIVED
                }
                handleCancel={() => {
                  if (duplicatedValues) {
                    navigate(-1);
                  } else {
                    resetForm({ values: initialValues });
                  }
                }}
                handleSubmit={() => submitForm()}
                isLoading={
                  isGeneratingThumbnail ||
                  isUpdateAnnouncementLoading ||
                  isCreateAnnouncementLoading
                }
              />
              <Preview
                open={openPreview}
                onClose={() => setOpenPreview(false)}
                announcement={values}
              />

              {/* Delete Confirmation Dialog */}
              {openDeleteDialog && (
                <ConfirmDialog
                  title='Delete announcement'
                  content={
                    <>
                      Are you sure you want to delete this announcement?
                      <br />
                      This action cannot be undone.
                    </>
                  }
                  onCancel={() => setOpenDeleteDialog(false)}
                  onOk={handleConfirmDelete}
                  isLoading={isDeleteAnnouncementLoading}
                />
              )}

              {/* End Post Confirmation Dialog */}
              {openEndPostDialog && (
                <ConfirmDialog
                  title='End post now'
                  content={
                    <>
                      Are you sure you want to end this announcement now?
                      <br />
                      It will no longer be visible to users once it is ended.
                    </>
                  }
                  onCancel={() => setOpenEndPostDialog(false)}
                  onOk={handleConfirmEndPost}
                  isLoading={isEndPostAnnouncementLoading}
                />
              )}

              {/* Thumbnail Generator - Hidden component for generating thumbnails */}
              {generateThumbnail && pendingSubmitValues && (
                <ThumbnailGenerator
                  announcement={pendingSubmitValues}
                  onGenerated={handleThumbnailGenerated}
                />
              )}
            </Form>
          )}
        </Formik>
      </AnnouncementDetailProvider>
    </Layout>
  );
};

export default CreateOrEdit;
